import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, X, ArrowRight, Shield, Clock, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
];

const ALLOWED_EXTENSIONS = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx', 'png', 'jpg', 'jpeg'];

export default function PortfolioUploadPage() {
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const validateFile = (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`File type ".${ext}" not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit.');
      return false;
    }
    setError('');
    return true;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected && validateFile(selected)) {
      setFile(selected);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file to upload.'); return; }
    if (!formData.name.trim()) { setError('Please enter your name.'); return; }
    if (!formData.email.trim()) { setError('Please enter your email.'); return; }

    setUploading(true);
    setError('');

    const body = new FormData();
    body.append('file', file);
    body.append('name', formData.name);
    body.append('email', formData.email);
    body.append('phone', formData.phone);
    body.append('notes', formData.notes);

    try {
      const res = await fetch(`${API_URL}/api/portfolio/upload`, { method: 'POST', body });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
      } else {
        setError(data.detail || 'Upload failed. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (result) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="max-w-2xl mx-auto px-6">
            <div className="text-center space-y-6" data-testid="upload-success">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Portfolio Received!</h1>
              <p className="text-white/60 text-lg max-w-md mx-auto">
                Thank you, <span className="text-white font-medium">{formData.name}</span>. Our investment experts will review your portfolio and reach out to you within 24-48 hours.
              </p>
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-left space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">Reference ID</span>
                  <span className="text-white font-mono text-xs">{result.submission_id?.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">File</span>
                  <span className="text-white">{file?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">Email</span>
                  <span className="text-white">{formData.email}</span>
                </div>
              </div>
              <a
                href="/"
                data-testid="back-to-home-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 mt-4"
              >
                Back to Home
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-white/60 text-sm">Portfolio Review Service</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white" data-testid="portfolio-upload-heading">
              Upload Your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                Portfolio
              </span>
            </h1>
            <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto">
              Share your current investment portfolio and our experts will provide a detailed analysis with personalized recommendations.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-10">
            {/* Form Column */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="portfolio-upload-form">
                {/* Drop Zone */}
                <div
                  data-testid="file-drop-zone"
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-300 cursor-pointer ${
                    dragActive
                      ? 'border-emerald-400 bg-emerald-500/10'
                      : file
                      ? 'border-emerald-500/50 bg-emerald-500/5'
                      : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'
                  }`}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <input
                    id="fileInput"
                    type="file"
                    className="hidden"
                    accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />

                  {file ? (
                    <div className="flex items-center justify-center gap-4">
                      <div className="p-3 bg-emerald-500/20 rounded-xl">
                        <FileText className="w-8 h-8 text-emerald-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium" data-testid="selected-file-name">{file.name}</p>
                        <p className="text-white/40 text-sm">{formatSize(file.size)}</p>
                      </div>
                      <button
                        type="button"
                        data-testid="remove-file-btn"
                        onClick={(e) => { e.stopPropagation(); setFile(null); }}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-white/40" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto">
                        <Upload className="w-8 h-8 text-white/60" />
                      </div>
                      <p className="text-white/80 font-medium">Drop your portfolio file here</p>
                      <p className="text-white/40 text-sm">or click to browse. PDF, Excel, CSV, Word, Images — up to 10MB</p>
                    </div>
                  )}
                </div>

                {/* Form Fields */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Full Name *</label>
                    <input
                      type="text"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-500/50 transition-colors"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1.5 block">Email *</label>
                    <input
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-500/50 transition-colors"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/60 text-sm mb-1.5 block">Phone (Optional)</label>
                  <input
                    type="tel"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-500/50 transition-colors"
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div>
                  <label className="text-white/60 text-sm mb-1.5 block">Notes (Optional)</label>
                  <textarea
                    data-testid="input-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-500/50 transition-colors resize-none"
                    placeholder="Any specific concerns or goals you'd like us to focus on..."
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 px-4 py-3 rounded-xl" data-testid="upload-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={uploading}
                  data-testid="submit-portfolio-btn"
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Submit Portfolio for Review
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Info Column */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 space-y-6">
                <h3 className="text-white font-semibold text-lg">How it works</h3>
                <div className="space-y-5">
                  {[
                    { icon: Upload, title: 'Upload', desc: 'Share your current portfolio statement or holdings summary.' },
                    { icon: Users, title: 'Expert Review', desc: 'Our investment team analyzes your portfolio in detail.' },
                    { icon: Clock, title: 'Get Insights', desc: 'Receive a personalized report with recommendations within 48 hours.' },
                  ].map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                        <step.icon className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{step.title}</p>
                        <p className="text-white/40 text-sm">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-white font-semibold">Your Data is Secure</h3>
                </div>
                <p className="text-white/40 text-sm leading-relaxed">
                  All uploaded documents are encrypted and accessible only to our authorized investment team. We never share your financial data with third parties.
                </p>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-white font-semibold mb-2">Accepted Formats</h3>
                <div className="flex flex-wrap gap-2">
                  {['PDF', 'Excel', 'CSV', 'Word', 'Images'].map((fmt) => (
                    <span key={fmt} className="px-3 py-1 bg-white/10 rounded-full text-white/60 text-xs font-medium">
                      {fmt}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
